import { useEffect, useState, useMemo } from "react";
import {
  Wallet, Building2, FileClock, MapPin, Search, X, Plus, RefreshCw,
  ArrowUpRight, ArrowDownRight, Check, Download, Share2 } from "lucide-react";
import ShareModal from "../components/ShareModal";
import {
  getCredits, getActiveProfile, getOwnedCities, searchCities, openCity, renewCity,
  listOwnedZones, getZoneVersions, getZoneVersion,
} from "../lib/api";
import {
  DeepAnalysisReport, DeepAnalysisHero, exportDeepAnalysisPDF, deepAnalysisTicket, deriveDeepAnalysisView,
} from "./DeepAnalysis";

const rp = (n) => "Rp " + Number(n ?? 0).toLocaleString("id-ID");
const fmtDate = (s) => new Date(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const fmtDateTime = (s) => new Date(s).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
const daysLeft = (s) => Math.ceil((new Date(s) - new Date()) / 86400000);

const KIND_LABEL = {
  topup: "Isi Saldo", adjustment: "Penyesuaian", refund: "Pengembalian Dana",
  city_open: "Kota dibuka", city_recurring: "Kota diperpanjang",
  deep_analysis_base: "Analisis Mendalam", deep_analysis_update: "Pembaruan Analisis Mendalam",
};

const TABS = [
  { key: "credits", label: "Kredit", icon: Wallet },
  { key: "cities", label: "Kota Dimiliki", icon: Building2 },
  { key: "reports", label: "Riwayat Laporan", icon: FileClock },
];

export default function Account() {
  const [tab, setTab] = useState("credits");
  const [balance, setBalance] = useState(null);
  const [profile, setProfile] = useState(null);

  async function refreshHeader() {
    try {
      const [c, p] = await Promise.all([getCredits(), getActiveProfile().catch(() => null)]);
      setBalance(c.balance);
      setProfile(p);
    } catch { /* not authed */ }
  }
  useEffect(() => { refreshHeader(); }, []);

  return (
    <div className="min-h-full bg-[#f8f9fa]">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-800">Kredit &amp; Tagihan</h1>
            <p className="text-sm text-slate-400 mt-1">
              {profile?.businessName ? `${profile.businessName} · ` : ""}dompet, langganan kota, dan riwayat laporan Anda
            </p>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 shadow-sm px-6 py-4 text-right">
            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-400 justify-end">
              <Wallet className="w-3.5 h-3.5" /> Saldo
            </div>
            <div className="text-2xl font-extrabold text-slate-800 tabular-nums mt-1">
              {balance == null ? "—" : rp(balance)}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 mb-6">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                tab === key ? "border-brand-600 text-brand-700" : "border-transparent text-slate-400 hover:text-slate-600"
              }`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {tab === "credits" && <CreditsTab />}
        {tab === "cities" && <CitiesTab profile={profile} onChange={refreshHeader} />}
        {tab === "reports" && <ReportsTab />}
      </div>
    </div>
  );
}

// ─── Credits ────────────────────────────────────────────────────────────────
function CreditsTab() {
  const [ledger, setLedger] = useState(null);
  useEffect(() => { getCredits().then((c) => setLedger(c.ledger)).catch(() => setLedger([])); }, []);

  if (!ledger) return <Loading />;
  if (ledger.length === 0) return <Empty icon={Wallet} title="Belum ada transaksi" hint="Isi saldo dan pengeluaran akan muncul di sini." />;

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[520px]">
          <thead>
            <tr className="border-b border-slate-100 text-left">
              <th className="px-5 py-3 font-semibold text-slate-400 text-xs">Waktu</th>
              <th className="px-5 py-3 font-semibold text-slate-400 text-xs">Jenis</th>
              <th className="px-5 py-3 font-semibold text-slate-400 text-xs text-right">Jumlah</th>
              <th className="px-5 py-3 font-semibold text-slate-400 text-xs text-right">Saldo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {ledger.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50">
                <td className="px-5 py-3 text-slate-500 whitespace-nowrap">{fmtDateTime(e.created_at)}</td>
                <td className="px-5 py-3 text-slate-700">
                  <span className="inline-flex items-center gap-1.5">
                    {e.delta >= 0 ? <ArrowUpRight className="w-3.5 h-3.5 text-green-500" /> : <ArrowDownRight className="w-3.5 h-3.5 text-red-500" />}
                    {KIND_LABEL[e.kind] || e.kind}
                  </span>
                  {e.note && <span className="block text-[11px] text-slate-400 ml-5">{e.note}</span>}
                </td>
                <td className={`px-5 py-3 text-right font-semibold tabular-nums ${e.delta >= 0 ? "text-green-600" : "text-red-600"}`}>
                  {e.delta >= 0 ? "+" : "−"}{rp(Math.abs(e.delta))}
                </td>
                <td className="px-5 py-3 text-right text-slate-500 tabular-nums">{rp(e.balance_after)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Price confirmation ─────────────────────────────────────────────────────
// Every action that moves credit shows the amount first (house rule). Opening a
// city used to charge a year on the first click of "Buka".
function ConfirmCharge({ title, body, amount, confirmLabel = "Bayar", busy, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <h3 className="font-bold text-slate-800">{title}</h3>
        <p className="text-sm text-slate-500 mt-2 leading-relaxed">{body}</p>
        <div className="mt-4 flex items-center justify-between rounded-xl bg-slate-50 border border-slate-100 px-4 py-3">
          <span className="text-xs text-slate-500">Dipotong dari kredit</span>
          <span className="text-sm font-bold text-slate-800 tabular-nums">{rp(amount)}</span>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} disabled={busy} className="px-4 py-2 rounded-xl border border-slate-300 text-xs font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Batal</button>
          <button onClick={onConfirm} disabled={busy} className="px-4 py-2 rounded-xl bg-brand-700 text-white text-xs font-bold hover:bg-brand-800 disabled:opacity-50">{busy ? "Memproses…" : confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Owned cities ───────────────────────────────────────────────────────────
function CitiesTab({ profile, onChange }) {
  const [cities, setCities] = useState(null);
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState(null);
  const [renewAsk, setRenewAsk] = useState(null);   // city row awaiting confirmation

  async function load() {
    if (!profile?.id) { setCities([]); return; }
    try { setCities(await getOwnedCities(profile.id)); } catch { setCities([]); }
  }
  useEffect(() => { load(); }, [profile?.id]);

  async function doRenew(kode) {
    setBusy(kode);
    try { await renewCity(profile.id, kode); await load(); onChange?.(); setRenewAsk(null); }
    catch (e) { alert(e.message); } finally { setBusy(null); }
  }

  if (!profile?.id) return <Empty icon={Building2} title="Belum ada profil aktif" hint="Selesaikan onboarding terlebih dahulu." />;
  if (!cities) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">Kota milik <span className="font-semibold text-slate-700">{profile.businessName || "profil ini"}</span></p>
        <button onClick={() => setPicker(true)}
          className="flex items-center gap-2 rounded-xl bg-brand-700 px-4 py-2 text-xs font-bold text-white hover:bg-brand-800 transition-colors">
          <Plus className="w-3.5 h-3.5" /> Buka Kota
        </button>
      </div>

      {cities.length === 0 ? (
        <Empty icon={Building2} title="Belum ada kota" hint="Buka kota untuk membuka peta analisis dan analisis mendalamnya." />
      ) : (
        <div className="grid gap-3">
          {cities.map((c) => {
            const left = daysLeft(c.expires_at);
            return (
              <div key={c.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-brand-600" />
                    <span className="font-bold text-slate-800">{c.nama_kabkot}</span>
                    <span className="text-xs text-slate-400">{c.nama_provinsi}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Dibuka {fmtDate(c.opened_at)} · {rp(c.amount_charged)} · periode {c.period_no}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {c.is_active ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-green-600">
                      <Check className="w-3.5 h-3.5" /> Aktif · {left} hari lagi
                    </span>
                  ) : (
                    <span className="text-xs font-bold text-red-500">Kedaluwarsa</span>
                  )}
                  <div className="text-[11px] text-slate-400 mt-0.5">Berakhir {fmtDate(c.expires_at)}</div>
                  <button onClick={() => setRenewAsk(c)} disabled={busy === c.kode_kabkot}
                    className="mt-2 flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-[11px] font-bold text-slate-600 hover:bg-slate-50 transition-colors disabled:opacity-50 ml-auto">
                    <RefreshCw className="w-3 h-3" /> {busy === c.kode_kabkot ? "…" : "Perpanjang"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {renewAsk && (
        <ConfirmCharge
          title={`Perpanjang ${renewAsk.nama_kabkot}?`}
          body={`Masa berlaku ${renewAsk.nama_kabkot} diperpanjang satu tahun dari tanggal berakhirnya. Tarif mengikuti harga kota saat ini; periode terakhir ${rp(renewAsk.amount_charged)}.`}
          amount={renewAsk.amount_charged} confirmLabel="Perpanjang & bayar" busy={busy === renewAsk.kode_kabkot}
          onConfirm={() => doRenew(renewAsk.kode_kabkot)} onCancel={() => setRenewAsk(null)} />
      )}

      {picker && <CityPicker profileId={profile.id}
        ownedKodes={(cities || []).filter((c) => c.is_active).map((c) => c.kode_kabkot)}
        onClose={() => setPicker(false)}
        onOpened={async () => { setPicker(false); await load(); onChange?.(); }} />}
    </div>
  );
}

function CityPicker({ profileId, ownedKodes = [], onClose, onOpened }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(null);

  // Cities the profile already owns (active) shouldn't appear — you can't re-open them.
  const owned = useMemo(() => new Set(ownedKodes), [ownedKodes]);
  const visible = useMemo(() => results.filter((c) => !owned.has(c.kode_kabkot)), [results, owned]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => {
      searchCities(q, 20).then((r) => { if (alive) { setResults(r); setLoading(false); } }).catch(() => alive && setLoading(false));
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  const [ask, setAsk] = useState(null);   // city awaiting price confirmation

  async function open(kode) {
    setOpening(kode);
    try { await openCity(profileId, kode); setAsk(null); onOpened(); }
    catch (e) { alert(e.message); setOpening(null); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800">Buka Kota</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2 bg-slate-100 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-slate-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kota atau provinsi…"
              className="bg-transparent text-sm flex-1 outline-none" />
          </div>
        </div>
        <div className="overflow-y-auto">
          {loading ? <div className="py-10 text-center text-sm text-slate-400">Mencari…</div> :
            visible.length === 0 ? <div className="py-10 text-center text-sm text-slate-400">
              {results.length > 0 ? "Anda sudah memiliki kota yang cocok." : "Kota tidak ditemukan."}
            </div> :
            visible.map((c) => (
              <div key={c.kode_kabkot} className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-50 hover:bg-slate-50">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-800 text-sm truncate">{c.nama_kabkot}</p>
                  <p className="text-[11px] text-slate-400">{c.nama_provinsi} · {Number(c.total_population).toLocaleString("id-ID")} jiwa</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-slate-800 tabular-nums">{rp(c.price)}<span className="text-[10px] font-normal text-slate-400">/thn</span></p>
                  {c.discount_pct > 0 && <p className="text-[10px] text-green-600">−{c.discount_pct}% diterapkan</p>}
                </div>
                <button onClick={() => setAsk(c)} disabled={opening === c.kode_kabkot}
                  className="rounded-lg bg-brand-700 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-brand-800 transition-colors disabled:opacity-50 shrink-0">
                  {opening === c.kode_kabkot ? "…" : "Buka"}
                </button>
              </div>
            ))}
        </div>
      </div>
      {ask && (
        <ConfirmCharge
          title={`Buka ${ask.nama_kabkot}?`}
          body={`Kota ${ask.nama_kabkot} (${ask.nama_provinsi}) terbuka untuk profil ini selama satu tahun: peta skor, analisis mendalam, dan laporan Skenario di dalamnya.${ask.discount_pct > 0 ? ` Diskon ${ask.discount_pct}% sudah diterapkan.` : ""}`}
          amount={ask.price} confirmLabel="Buka & bayar" busy={opening === ask.kode_kabkot}
          onConfirm={() => open(ask.kode_kabkot)} onCancel={() => setAsk(null)} />
      )}
    </div>
  );
}

// ─── Report history ─────────────────────────────────────────────────────────
function ReportsTab() {
  const [zones, setZones] = useState(null);
  const [viewer, setViewer] = useState(null); // { gridId, versionNo }

  useEffect(() => { listOwnedZones().then(setZones).catch(() => setZones([])); }, []);

  if (!zones) return <Loading />;
  if (zones.length === 0) return <Empty icon={FileClock} title="Belum ada grid yang dimiliki" hint="Jalankan analisis mendalam untuk membangun riwayat laporan." />;

  return (
    <div className="space-y-3">
      {zones.map((z) => <ZoneVersions key={z.gridId} zone={z} onView={setViewer} />)}
      {viewer && <VersionViewer {...viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}

function ZoneVersions({ zone, onView }) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState(null);

  async function toggle() {
    const next = !open; setOpen(next);
    if (next && !versions) {
      try { setVersions(await getZoneVersions(zone.gridId)); } catch { setVersions([]); }
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <button onClick={toggle} className="w-full flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors text-left">
        <div>
          <p className="font-bold text-slate-800 font-mono text-sm">grid-{zone.gridId}</p>
          <p className="text-[11px] text-slate-400">
            {[zone.namaKelurahan, zone.namaKabkot].filter(Boolean).join(", ") || "—"}
            {zone.subcategory ? ` · ${zone.subcategory}` : ""}
          </p>
        </div>
        <span className="text-[11px] font-bold text-slate-400">{open ? "Sembunyikan" : "Riwayat"}</span>
      </button>
      {open && (
        <div className="border-t border-slate-100">
          {!versions ? <div className="py-6 text-center text-xs text-slate-400">Memuat…</div> :
            versions.length === 0 ? <div className="py-6 text-center text-xs text-slate-400">Belum ada versi yang tercatat.</div> :
            versions.map((v) => (
              <button key={`${v.version_no}-${v.created_at}`} onClick={() => onView({ zone, versionNo: v.version_no })}
                className="w-full flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-50 last:border-0 hover:bg-teal-50/50 transition-colors text-left">
                <div className="flex items-center gap-3">
                  <span className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-[11px] font-bold text-slate-500">v{v.version_no ?? "?"}</span>
                  <div>
                    <p className="text-xs font-semibold text-slate-700 capitalize">{v.event_type}{v.radius ? ` · ${v.radius}` : ""}</p>
                    <p className="text-[10px] text-slate-400">{fmtDateTime(v.created_at)}</p>
                  </div>
                </div>
                <span className="text-[11px] text-slate-400">{v.amount_charged != null ? rp(v.amount_charged) : "—"}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

// The business name on a shared link's header, from the cached profile.
function ownerLabel() {
  try { return JSON.parse(localStorage.getItem("landhep_profile") || "{}").businessName || null; } catch { return null; }
}

function VersionViewer({ zone, versionNo, onClose }) {
  const gridId = zone.gridId;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  useEffect(() => { getZoneVersion(gridId, versionNo).then(setData).catch(() => setErr(true)); }, [gridId, versionNo]);

  const result      = data?.result ?? null;
  const hasReport   = !!result?.analysis;
  const radius      = data?.radius;
  const generatedAt = data?.created_at ? Date.parse(data.created_at) : null;
  const view        = hasReport ? deriveDeepAnalysisView(zone, result) : null;

  // Same PDF as the live page — one entry point, so any report change flows through.
  // The print page renders its own maps, so a history download carries them too.
  async function handleDownload() {
    if (downloading || !view?.analysis) return;
    setDownloading(true);
    try {
      exportDeepAnalysisPDF({
        analysis: view.analysis, fitView: view.fitView, result, profileView: view.profileView,
        yourFit: view.yourFit, score: view.score, idx: view.idx, verdict: view.verdict,
        gridLabel: view.gridLabel, fullLocation: view.fullLocation, namaKabkot: view.namaKabkot,
        subcategory: view.subcategory, target_ses: view.targetSes, radius,
        resolution: view.resolution, lat: view.lat, lng: view.lng, generatedAt,
        audit: { gridId, meta: {
          resolution: view.resolution, radius, lat: view.lat, lng: view.lng,
          kode_kelurahan: zone?.kode_kelurahan ?? null,
        } },
      });
    } catch (e) {
      alert(`Gagal membuat PDF: ${e.message}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 shrink-0">
          <div>
            <h2 className="font-bold text-slate-800 font-mono text-sm">grid-{gridId} · v{versionNo}</h2>
            {data && <p className="text-[11px] text-slate-400">{fmtDateTime(data.created_at)} · {data.event_type}{data.radius ? ` · ${data.radius}` : ""}</p>}
          </div>
          <div className="flex items-center gap-2">
            {hasReport && (
              <>
                <button onClick={() => setShareOpen(true)}
                  className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50 transition-colors">
                  <Share2 className="w-3.5 h-3.5" /> Bagikan
                </button>
                <ShareModal open={shareOpen} onClose={() => setShareOpen(false)}
                  kind="klasik" gridId={gridId} title={`${view.gridLabel} · v${versionNo} · ${view.fullLocation || ""}`}
                  ownerLabel={ownerLabel()}
                  buildPayload={() => deepAnalysisTicket({
                    analysis: view.analysis, fitView: view.fitView, result, profileView: view.profileView,
                    yourFit: view.yourFit, score: view.score, idx: view.idx, verdict: view.verdict,
                    gridLabel: view.gridLabel, fullLocation: view.fullLocation, namaKabkot: view.namaKabkot,
                    subcategory: view.subcategory, target_ses: view.targetSes, radius,
                    resolution: view.resolution, lat: view.lat, lng: view.lng, generatedAt,
                  })} />
                <button onClick={handleDownload} disabled={downloading}
                  className="flex items-center gap-2 rounded-xl bg-brand-700 px-4 py-2 text-xs font-bold text-white hover:bg-brand-800 transition-colors disabled:opacity-50">
                  <Download className="w-3.5 h-3.5" />
                  {downloading ? "Membuat PDF…" : "Unduh PDF"}
                </button>
              </>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
          </div>
        </div>
        <div className="overflow-y-auto bg-slate-100">
          {err ? <p className="p-5 text-sm text-red-500">Gagal memuat versi ini.</p> :
            !data ? <p className="p-5 text-sm text-slate-400">Memuat…</p> :
            hasReport ? (
              // Same header + sections as the live Deep Analysis page — one source of truth.
              <>
                <DeepAnalysisHero
                  gridLabel={view.gridLabel} fullLocation={view.fullLocation} result={result}
                  resolution={view.resolution} lat={view.lat} lng={view.lng} verdict={view.verdict}
                  score={view.score} idx={view.idx} yourFit={view.yourFit} subcategory={view.subcategory}
                  targetSes={view.targetSes} radius={radius} generatedAt={generatedAt}
                />
                <div className="max-w-6xl mx-auto px-8 py-8">
                  <DeepAnalysisReport result={result} targetSes={view.targetSes} radius={radius} profileView={view.profileView} />
                </div>
              </>
            ) : (
              // Legacy snapshots (pre-render) without a structured analysis block.
              <div className="p-5">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">Cuplikan Lengkap</p>
                <pre className="bg-white border border-slate-200 rounded-xl p-3 text-[11px] text-slate-600 overflow-x-auto whitespace-pre-wrap">
{JSON.stringify(result, null, 2)}
                </pre>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

// ─── Shared ─────────────────────────────────────────────────────────────────
function Loading() { return <div className="py-16 text-center text-sm text-slate-400">Memuat…</div>; }
function Empty({ icon: Icon, title, hint }) {
  return (
    <div className="py-16 text-center">
      <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-3"><Icon className="w-6 h-6 text-slate-300" /></div>
      <p className="text-sm font-bold text-slate-600">{title}</p>
      <p className="text-xs text-slate-400 mt-1">{hint}</p>
    </div>
  );
}
